import { createStyles, makeStyles } from '@material-ui/core/styles';
import type { Theme } from '@material-ui/core/styles';
import React, { useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import clsx from 'clsx';
import { Link as RouterLink, useRouteMatch } from 'react-router-dom';
import {
  catalogueTaxonomiesHierarchySelector,
  ProductTaxonomyTypeHierarchyNode,
} from '../../../api/products/taxonomyTypes';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
// import { localeState } from '../../../api/appState';
import { catalogueGlobalFilterState } from '../../../api/products/catalogue';

export type MainHorisontalNavigationProps = {
  classes?: Partial<Record<MainHorisontalNavigationClassKey, string>>;
};

export type MainHorisontalNavigationClassKey =
  | 'root'
  | 'category'
  | 'currentCategory'
  | 'categoryText'
  | 'selectedCategory'
  | 'links'
  | 'hidden'
  | 'subCategories'
  | 'subCategory'
  | 'subCategoryLabel'
  | 'subCategoryLabel'
  | 'subCategoryChildLink'
  | 'subCategoriesChildren';

const useStyles = makeStyles((theme: Theme) =>
  createStyles<MainHorisontalNavigationClassKey, {}>({
    root: {
      display: 'none',
      gap: theme.spacing(0.5),
      paddingLeft: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
      [theme.breakpoints.up('lg')]: {
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(0, 2),
      },
      textDecoration: 'none',
      width: '100%',
      alignItems: 'center',
    },
    category: {
      height: theme.spacing(5),
      width: 'auto',
      minWidth: '82px',
      [theme.breakpoints.up('lg')]: {
        width: 'auto',
        minWidth: theme.spacing(12),
      },
      gap: theme.spacing(0.5),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      color: '#fff',
      fontWeight: 600,
      padding: theme.spacing(0, 2),
      borderRadius: '20px',

      '&:hover': {
        backgroundColor: '#FFA800',
        color: '#fff',
        textDecoration: 'none!important',
      },
    },
    currentCategory: {
      backgroundColor: '#FFA800',
      color: '#fff',
    },
    categoryText: {
      fontSize: '14px',
      [theme.breakpoints.up('lg')]: {
        fontSize: '16px',
      },
    },
    selectedCategory: {
      backgroundColor: '#FFA800',
      color: '#fff',
    },
    links: {
      textDecoration: 'none',
      '& :hover': {
        textDecoration: 'none',
      },
    },
    hidden: {
      display: 'none',
    },
    subCategories: {
      position: 'absolute',
      top: theme.spacing(8),
      left: 0,
      width: '100%',
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: '#F4F4F4',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-evenly',
      gap: theme.spacing(2),
      flexWrap: 'wrap',
      padding: theme.spacing(3, 2),
      fontSize: '24px',
      boxShadow: theme.shadows[3],
      borderRadius: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        width: '80%',
        left: '10%',
      },
      [theme.breakpoints.up('xl')]: {
        width: '60%',
        left: '20%',
      },
    },
    subCategoryLabel: {
      color: '#000',
      fontWeight: 600,
      fontSize: '14px',
    },
    subCategory: {
      flex: 1,
      width: '100%',
      minWidth: '200px',
    },
    subCategoriesChildren: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      fontWeight: 400,
      fontSize: '16px',
    },
    subCategoryChildLink: {
      fontSize: '12px',
      fontWeight: 400,
      color: '#000',
    },
  }),
);

export default function MainHorisontalNavigation({ classes: pClasses }: MainHorisontalNavigationProps): JSX.Element {
  const classes = useStyles({ classes: pClasses });
  const taxonomyTree = useRecoilValue(catalogueTaxonomiesHierarchySelector);

  const taxonomies = taxonomyTree.children || [];

  const [activeTaxonomy, setActiveTaxonomy] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (code: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveTaxonomy(code);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setActiveTaxonomy(null);
    }, 300);
  };

  return (
    <div className={classes.root}>
      {taxonomies.map((node) => (
        <MainHorisontalNavigationItem
          key={node.code}
          {...node}
          isActive={activeTaxonomy === node.code}
          onMouseEnter={() => handleMouseEnter(node.code)}
          onMouseLeave={handleMouseLeave}
        />
      ))}
    </div>
  );
}

interface MainHorisontalNavigationItemProps extends ProductTaxonomyTypeHierarchyNode {
  isActive: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function MainHorisontalNavigationItem({
  isActive,
  onMouseEnter,
  onMouseLeave,
  ...taxonomy
}: MainHorisontalNavigationItemProps): JSX.Element {
  const classes = useStyles();
  const t = useTranslateWithFallback();
  // const locale = useRecoilValue(localeState);
  const setFilter = useSetRecoilState(catalogueGlobalFilterState);
  const { url } = useRouteMatch();

  // const to = `/${locale}${taxonomy.code}`;
  const to = taxonomy.code;

  const handleClick: () => void = () => {
    setFilter({ searchText: undefined });
  };

  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <RouterLink
        className={clsx(classes.category, classes.links, {
          [classes.selectedCategory]: url.startsWith(to),
          [classes.currentCategory]: isActive,
        })}
        onClick={handleClick}
        to={to}
      >
        <div className={classes.categoryText}>{t(taxonomy.label)}</div>
      </RouterLink>
      {taxonomy.children && taxonomy.children.length > 0 && (
        <div className={isActive ? classes.subCategories : classes.hidden}>
          {taxonomy.children.map((subCategory) => (
            <div key={subCategory.code} className={classes.subCategory}>
              <RouterLink
                className={clsx(classes.links, classes.subCategoryLabel)}
                onClick={handleClick}
                // to={`/${locale}${subCategory.code}`}
                to={subCategory.code}
              >
                <div>{t(subCategory.label)}</div>
              </RouterLink>
              {subCategory.children && subCategory.children.length > 0 && (
                <div className={classes.subCategoriesChildren}>
                  {subCategory.children.map((tx) => (
                    <RouterLink
                      className={clsx(classes.links, classes.subCategoryChildLink)}
                      key={tx.code}
                      onClick={handleClick}
                      // to={`/${locale}${tx.code}`}
                      to={tx.code}
                    >
                      <div>{t(tx.label)}</div>
                    </RouterLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
